import React, { useState } from 'react';
import { OptionHistoryChart } from './OptionHistoryChart';
import { OptionHistoryIntervalSelector } from './OptionHistoryIntervalSelector';
import { OptionHistoryMetricSelector } from './OptionHistoryMetricSelector';
import { useOptionHistoryData } from '../hooks/useOptionHistoryData';
import { OptionHistoryInterval, OptionHistoryMetricKey } from '../types/StockData';

export const OptionHistoryPage: React.FC = () => {
  const [optionContract, setOptionContract] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedInterval, setSelectedInterval] = useState<OptionHistoryInterval>(5);
  const [selectedMetric, setSelectedMetric] = useState<OptionHistoryMetricKey>('volume');

  const { data, loading, error, refetch } = useOptionHistoryData(optionContract, selectedInterval);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setOptionContract(inputValue.trim().toUpperCase());
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!data || !data.chains || data.chains.length === 0) return null;

    const values = data.chains
      .map(d => d[selectedMetric])
      .filter((v): v is number => v !== null && v !== undefined && Number.isFinite(v));

    if (values.length === 0) return null;

    const total = values.reduce((sum, val) => sum + val, 0);
    const average = total / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);

    return { total, average, max, min, count: values.length };
  }, [data, selectedMetric]);

  const formatNumber = (value: number): string => {
    if (selectedMetric === 'implied_volatility') {
      return `${(value * 100).toFixed(2)}%`;
    }
    if (Math.abs(value) >= 1e9) {
      return `${(value / 1e9).toFixed(2)}B`;
    } else if (Math.abs(value) >= 1e6) {
      return `${(value / 1e6).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1e3) {
      return `${(value / 1e3).toFixed(2)}K`;
    }
    return value.toFixed(0);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Option Contract History</h1>
              <p className="text-gray-600 mt-1">
                Search and analyze historical data for specific option contracts
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Option Contract Search */}
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value.toUpperCase())}
                  placeholder="e.g., SPXW251009C06450000"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono w-64"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Search
                </button>
              </form>

              {/* Refresh Button */}
              {optionContract && (
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg
                    className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OptionHistoryIntervalSelector
            selectedInterval={selectedInterval}
            onIntervalChange={setSelectedInterval}
          />
          
          {/* Summary Statistics */}
          {summaryStats && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Summary Statistics
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">
                    {formatNumber(summaryStats.average)}
                  </div>
                  <div className="text-xs text-gray-500">Average</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">
                    {formatNumber(summaryStats.max)}
                  </div>
                  <div className="text-xs text-gray-500">Maximum</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-red-600">
                    {formatNumber(summaryStats.min)}
                  </div>
                  <div className="text-xs text-gray-500">Minimum</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-600">
                    {summaryStats.count}
                  </div>
                  <div className="text-xs text-gray-500">Data Points</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800 font-medium">Error loading data: {error}</span>
            </div>
          </div>
        )}

        {/* Chart */}
        <OptionHistoryChart
          data={data?.chains || []}
          selectedMetric={selectedMetric}
          optionContract={optionContract || 'N/A'}
          isLoading={loading}
        />

        {/* Metric Selector */}
        <OptionHistoryMetricSelector
          selectedMetric={selectedMetric}
          onMetricChange={setSelectedMetric}
        />
      </div>
    </div>
  );
};
